import { Box, FormControlLabel, IconButton, Switch, Tooltip, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useLanguage } from '../../../../../context/LanguageContext';
import {
  NO_DISCOUNT_ID, OfferFormData, PREREQ_OPTIONS, TRIGGER_ROOT_OPTIONS, hasTriggerConditions, triggerConditionOptions, triggerFieldKinds,
} from './offerForm';
import OptionToggleGroup from './OptionToggleGroup';
import OfferGeneralFields from './OfferGeneralFields';
import DiscountSection from './DiscountSection';
import ConditionSection from './ConditionSection';

interface OfferCardProps {
  offer: OfferFormData;
  index: number;
  isUsed: boolean;
  isRecurring: boolean;
  isPreEdited: boolean;
  isCashdesk: boolean;
  onChange: (patch: Partial<OfferFormData>) => void;
  onRemove: () => void;
}

const OFFERS_KEY = 'pages.marketing.communication.offers';

const NO_TRIGGER_CONDITION = [{ id: 'none', label: 'Sans condition' }] as const;


function OfferCard({ offer, index, isUsed, isRecurring, isPreEdited, isCashdesk, onChange, onRemove }: OfferCardProps) {
  const { t } = useLanguage();
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          Offre {index + 1}{' '}
          <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 400 }}>
            (#NUM_COUPON_{index}#)
          </Typography>
        </Typography>
        {index > 0 && (
          <Tooltip
            title={isUsed
              ? `Cette offre est utilisée dans le builder (code #NUM_COUPON_${index}#) — retirez-la du contenu avant de la supprimer`
              : 'Supprimer cette offre'}
          >
            <span>
              <IconButton size="small" onClick={onRemove} disabled={isUsed} sx={{ color: isUsed ? 'text.disabled' : 'error.main' }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      <OfferGeneralFields offer={offer} onChange={onChange} isRecurring={isRecurring} showMaxEdit={isCashdesk} />
      <DiscountSection offer={offer} onChange={onChange} />

      {/* Les conditions n'apparaissent qu'une fois un type de remise choisi */}
      {offer.discountTypeId !== null && offer.discountTypeId !== NO_DISCOUNT_ID && (
        <>
          {/* L'édition conditionnée (montant, articles…) n'existe que pour les coupons Caisse */}
          {isCashdesk && !isPreEdited && (
            <ConditionSection
              title="Condition de l'édition"
              prefix="trigger"
              options={TRIGGER_ROOT_OPTIONS}
              value={offer.triggerId}
              // Les sous-conditions dépendent de la condition racine : on repart de « Sans condition »
              onSelect={triggerId => onChange({ triggerId, triggerConditions: [] })}
              offer={offer}
              onChange={onChange}
              fieldKinds={triggerFieldKinds(offer)}
              belowOptions={hasTriggerConditions(offer.triggerId) && (
                // « Sans condition » est exclusif ; les autres sous-conditions sont cumulables
                <Box sx={{ mt: 1.5, pl: 2, borderLeft: '2px solid', borderColor: 'divider', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <OptionToggleGroup
                    options={NO_TRIGGER_CONDITION}
                    value={offer.triggerConditions.length === 0 ? 'none' : null}
                    onChange={() => onChange({ triggerConditions: [] })}
                  />
                  <OptionToggleGroup
                    multiple
                    options={triggerConditionOptions(offer.triggerId)}
                    value={offer.triggerConditions}
                    onChange={triggerConditions => onChange({ triggerConditions })}
                  />
                </Box>
              )}
            />
          )}
          <ConditionSection
            title="Condition de l'utilisation"
            prefix="prereq"
            options={PREREQ_OPTIONS}
            value={offer.prereqId}
            onSelect={prereqId => onChange({ prereqId })}
            offer={offer}
            onChange={onChange}
            leading={(
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <FormControlLabel
                  control={(
                    <Switch
                      size="small"
                      checked={offer.immediateUse}
                      onChange={e => onChange({ immediateUse: e.target.checked })}
                    />
                  )}
                  label={t(`${OFFERS_KEY}.immediateUse`)}
                  sx={{ mr: 0.5, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                />
                <Tooltip title={t(`${OFFERS_KEY}.immediateUseHelp`)} arrow>
                  <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
            )}
          />
        </>
      )}
    </>
  );
}

export default OfferCard;
